/* eslint-disable react/prop-types */
import React from 'react';

interface TimeZoneViewProps {
  date?: Date;
  localeCodeWithCountry?: string;
  timeZone?: string;
  timeZoneType?: 'long' | 'short' | 'number' | 'abbr';
}

const INDIA_TIME_ZONES = ['Asia/Kolkata', 'Asia/Calcutta'];

const getResolvedTimeZone = (timeZone?: string): string => timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const getTimeZoneName = (date: Date, localeCodeWithCountry: string, resolvedTimeZone: string, type: string): string => {
  try {
    const parts = new Intl.DateTimeFormat(localeCodeWithCountry, {
      timeZone: resolvedTimeZone,
      timeZoneName: type as Intl.DateTimeFormatOptions['timeZoneName'],
      hour: '2-digit',
      minute: '2-digit'
    }).formatToParts(date);

    return parts.find((part) => part.type === 'timeZoneName')?.value?.trim() || '';
  } catch {
    return '';
  }
};

const getAbbrValue = (isIndiaTimeZone: boolean, shortName: string, shortOffset: string, resolvedTimeZone: string): string => {
  if (isIndiaTimeZone) return 'IST';
  return shortName || shortOffset || resolvedTimeZone;
};

const getNumberValue = (shortOffset: string, resolvedTimeZone: string): string => shortOffset.replace(/^GMT/, '') || resolvedTimeZone;

const getVisibleValue = ({
  timeZoneType,
  isIndiaTimeZone,
  shortName,
  shortOffset,
  longName,
  resolvedTimeZone
}: {
  timeZoneType: 'long' | 'short' | 'number' | 'abbr';
  isIndiaTimeZone: boolean;
  shortName: string;
  shortOffset: string;
  longName: string;
  resolvedTimeZone: string;
}): string => {
  switch (timeZoneType) {
    case 'abbr':
      return getAbbrValue(isIndiaTimeZone, shortName, shortOffset, resolvedTimeZone);
    case 'long':
      return longName || resolvedTimeZone;
    case 'number':
      return getNumberValue(shortOffset, resolvedTimeZone);
    case 'short':
    default:
      return shortName || shortOffset || resolvedTimeZone;
  }
};

const getAccessibleValue = (isIndiaTimeZone: boolean, longName: string, visible: string): string => {
  if (isIndiaTimeZone) return 'India Standard Time';
  return longName || visible;
};

const TimeZoneView: React.FC<TimeZoneViewProps> = ({ date = new Date(), localeCodeWithCountry = 'en-US', timeZone, timeZoneType = 'short' }) => {
  const resolvedTimeZone = getResolvedTimeZone(timeZone);
  const isIndiaTimeZone = INDIA_TIME_ZONES.includes(resolvedTimeZone);

  const shortName = getTimeZoneName(date, localeCodeWithCountry, resolvedTimeZone, 'short');
  const shortOffset = getTimeZoneName(date, localeCodeWithCountry, resolvedTimeZone, 'shortOffset');
  const longName = getTimeZoneName(date, localeCodeWithCountry, resolvedTimeZone, 'long');

  const visible = getVisibleValue({
    timeZoneType,
    isIndiaTimeZone,
    shortName,
    shortOffset,
    longName,
    resolvedTimeZone
  });

  const accessible = getAccessibleValue(isIndiaTimeZone, longName, visible);

  return (
    <span>
      <span aria-hidden="true">{visible}</span>
      <span
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0
        }}
      >
        {accessible}
      </span>
    </span>
  );
};

export default TimeZoneView;
